import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';

import defaultImg from '../../asset/project/default_1.png';
import { IMPORT_STATION_DETAIL_DESCRIPTION_MAX_LENGTH } from '../../shared/constants/limits';
import { WorkDetailSimpleData, WorkSimpleWithDetails } from '../../shared/dto/EntityRepository';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import { resolveUploadFileOriginUrl } from '../../shared/util/normalizeWorkData';
import { shrinkString } from '../../shared/util/shrinkString';

import BlockWithVisible from './BlockWithVisible';
import { useFormationSlotImport } from './useFormationSlotImport';

interface SimpleWorkDetailForFormationItemProps {
  parentWork: WorkSimpleWithDetails;
  data: WorkDetailSimpleData;
  showArtist: boolean;
  artistName: string;
}

const SimpleWorkDetailForFormationItem: React.FC<SimpleWorkDetailForFormationItemProps> = ({
  parentWork,
  data,
  showArtist,
  artistName,
}) => {
  const { importToSlot, isBulkImport, isSelected } = useFormationSlotImport();
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const imageUrl = useOptimizedImageUrl(resolveUploadFileOriginUrl(data.uploadFile), imgContainerRef);
  const isDetailSelected = isBulkImport && isSelected({
    workId: parentWork.id,
    detailId: data.id,
    originUrl: resolveUploadFileOriginUrl(data.uploadFile),
  });
  const shrinkedDescription = useMemo(
    () => shrinkString(data.description, IMPORT_STATION_DETAIL_DESCRIPTION_MAX_LENGTH),
    [data.description],
  );

  const onClickHandler = () => {
    importToSlot(
      {
        workId: parentWork.id,
        detailId: data.id,
        originUrl: resolveUploadFileOriginUrl(data.uploadFile),
        title: parentWork.title,
        prodYear: parentWork.prodYear,
        material: parentWork.material,
        size: parentWork.size,
        sizeText: parentWork.sizeText,
        dimensionsType: parentWork.dimensionsType,
        duration: parentWork.duration,
        artist: parentWork.artist,
      },
      parentWork,
      data,
    );
  };

  return (
    <SimpleWorkDetailForFormationItemComp
      key={data.id}
      $isSelected={isDetailSelected}
      aria-selected={isBulkImport ? isDetailSelected : undefined}
      onClick={() => onClickHandler()}
    >
      <ImageBlock>
        <Indicatior>{'└'}</Indicatior>
        <ImgWrapper ref={imgContainerRef}>
          <WorkImage src={imageUrl === '' ? defaultImg : imageUrl} alt="work detail" />
        </ImgWrapper>
      </ImageBlock>
      {showArtist && (
        <ArtistBlock>
          <span>{artistName}</span>
        </ArtistBlock>
      )}
      <TitleBlock title={data.description}>{shrinkedDescription}</TitleBlock>
      <BlockWithVisible width={'1.6'} value={data.detailType} />
      <BtnWrapper>-</BtnWrapper>
    </SimpleWorkDetailForFormationItemComp>
  );
};

const SimpleWorkDetailForFormationItemComp = styled.div<{ $isSelected: boolean }>`
  width: 100%;
  height: 50px;
  position: relative;
  display: flex;
  align-items: center;
  transition: all 0.3s ease-in-out;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  opacity: ${({ $isSelected }) => ($isSelected ? 0.5 : 1)};
  cursor: ${({ $isSelected }) => ($isSelected ? 'not-allowed' : 'pointer')};
`;

const ImageBlock = styled.div`
  height: 100%;
  flex: 0.8;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 10px;
`;

const Indicatior = styled.div`
  width: 10%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 20px;
  ${({ theme }) => theme.typography.Body_04};
`;

const ImgWrapper = styled.div`
  position: relative;
  width: 50%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WorkImage = styled.img`
  width: 100%;
  height: 100%;
  max-height: 80%;
  object-fit: contain;
`;

const ArtistBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  padding-left: 4px;
  ${({ theme }) => theme.typography.Body_04};

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const TitleBlock = styled.div`
  flex: 2;
  min-width: 0;
  display: flex;
  align-items: center;
  padding-left: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${({ theme }) => theme.typography.Body_04};
`;

const BtnWrapper = styled.div`
  flex: 0.2;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.typography.Body_04};
`;

export default SimpleWorkDetailForFormationItem;
